// 首頁「+ 新增」的動作面板 (W47.2)。
//
// **為什麼是一個面板而不是一個直達按鈕。** 這個按鈕的標籤是「新增」，行為卻是
// `nav("/diaries/new")` —— 標籤承諾任何東西，行為只兌現日記。想許一個心願的人點下去
// 得到的是一個日記編輯器，而且沒有任何一步告訴他剛才發生了什麼。
//
// **只有三個半目標能放進來。** 五個內容模組裡只有三個有專屬的 `/new` 路由:
// `/diaries/new` · `/wishes/new` · `/anniversaries/new`。相簿不是獨立模組 —— 照片是
// 心願與日記裡的塊，沒有 `/albums/new` 可去；日曆事件在頁面內的表單裡建，所以這一格
// 指向模組頁而不是假裝它有專屬路由。
//
// **`shoot-w47-2.mjs` 有一條斷言守這件事**：面板裡不該出現一個點下去只會把你送去模組
// 首頁、卻自稱「新增」的按鈕。

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { useSheetVariant } from "@/lib/hooks/useSheetVariant";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import { cn } from "@/lib/utils/cn";

export interface CreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSheet({ open, onOpenChange }: CreateSheetProps) {
  const nav = useNavigate();
  const { variant } = useSheetVariant();

  const go = (to: string) => {
    // Close before navigating: a sheet left open across a route change is the
    // orphan-panel shape `PillNavMenu` had to be taught not to have.
    onOpenChange(false);
    nav(to);
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      title={DASHBOARD_I18N.create.title}
      description={DASHBOARD_I18N.create.hint}
      data-testid="create-sheet"
    >
      <ul className="flex flex-col" data-testid="create-list">
        {CREATE_TARGETS.map(({ to, key, tone }) => (
          <li key={to}>
            <button
              type="button"
              onClick={() => go(to)}
              data-testid={`create-option-${key}`}
              aria-label={`${DASHBOARD_I18N.create[key].label}：${DASHBOARD_I18N.create[key].hint}`}
              className={cn(
                "press flex min-h-14 w-full items-center gap-s-3 rounded-md px-s-3 py-s-2 text-left",
                "hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  // `rounded-md`, and not `rounded-inner` — the same non-existent class that left
// the weather card's forecast boxes square a few hours ago, written a second
                // time from muscle memory. `hard-rule-audit` #17b caught it on the
                // first run of this file, which is the only argument for the rule.
                  "grid h-10 w-10 shrink-0 place-items-center rounded-md",
                  tone,
                )}
              >
                <IconFor name={key} />
              </span>
              <span className="min-w-0">
                {/* Two blocks, not one run of text. As inline spans these render
                    as 「寫日記 今天發生了什麼」 with no seam between the action and
                    its description — the same 「視覺狀態洩漏成交互狀態」 shape the
                    weather card's forecast labels had. */}
                <span className="block text-body font-bold text-ink-1">
                  {DASHBOARD_I18N.create[key].label}
                </span>
                <span className="mt-px block text-body-sm text-ink-3">
                  {DASHBOARD_I18N.create[key].hint}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </BottomSheet>
  );
}

/**
 * Line icons at 1.8, matching `renderModuleIcon` rather than `lucide`'s default
 * 2 — the nav and the sheet should draw the same weight for the same module.
 */
function IconFor({ name }: { name: keyof typeof DASHBOARD_I18N.create & string }) {
  const cls = "h-5 w-5";
  if (name === "diary") {
    return (
      <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H19a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H6.5A2.5 2.5 0 0 0 4 20.5z" />
        <path d="M4 5.5v15" />
      </svg>
    );
  }
  if (name === "wish") {
    return (
      <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z" />
      </svg>
    );
  }
  if (name === "anniversary") {
    return (
      <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 20s-7.5-4.6-9.2-9A5 5 0 0 1 12 6.6 5 5 0 0 1 21.2 12c-1.7 4.4-9.2 9-9.2 9z" />
      </svg>
    );
  }
  return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.5" y="5" width="17" height="16" rx="3" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  );
}

const CREATE_TARGETS = [
  { to: "/diaries/new", key: "diary" as const, tone: "bg-tint-ice" },
  { to: "/wishes/new", key: "wish" as const, tone: "bg-tint-blush" },
  { to: "/anniversaries/new", key: "anniversary" as const, tone: "bg-[#FDF3D8]" },
  { to: "/calendar", key: "event" as const, tone: "bg-[#E6F5EC]" },
];

/**
 * The `PageHero` trailing button **and** the sheet it opens, in one place.
 *
 * They were split across a route and a sibling file, which meant the route held
 * a piece of state and a sheet element for something it does not own — and it
 * pushed `Dashboard.tsx` back over `AGENTS.md §3 #10`'s 15 KB ceiling. A control
 * and the surface it controls belong together; that is not only about the byte
 * count.
 */
export function DashboardCreateAction() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={DASHBOARD_I18N.create.title}
        data-testid="dashboard-open-create"
        className="press cta-pill hover:opacity-90 transition-opacity"
      >
        <Plus size={18} aria-hidden />
        {DASHBOARD_I18N.create.title}
      </button>
      <CreateSheet open={open} onOpenChange={setOpen} />
    </>
  );
}

export { Plus };

export default CreateSheet;
